import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { Navigate } from 'react-router-dom';
import { 
  Crown, 
  Users, 
  Trophy, 
  MapPin, 
  Camera, 
  Megaphone,
  Check,
  Star,
  Zap,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const AccountUpgrade = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const isMobile = useIsMobile();

  // Check if user is authenticated
  const isAuthenticated = !!(supabaseUser || internalUser);
  const userProfile = profile || internalProfile;
  const currentRole = userProfile?.role;

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const upgradePlans = [
    {
      id: 'coach',
      title: 'Coach Account',
      price: '$9.99/month',
      popular: true,
      features: [
        'Manage team rosters',
        'Create and schedule games',
        'Access coaching tools',
        'Team communication features',
        'Player performance tracking',
        'Practice planning tools'
      ],
      icon: <Users className="h-6 w-6" />,
      description: 'Perfect for coaches who want to manage teams and players effectively'
    },
    {
      id: 'team-owner',
      title: 'Team Owner Account',
      price: '$19.99/month',
      popular: false,
      features: [
        'All Coach features',
        'Team financial management',
        'Multiple team management',
        'Advanced analytics',
        'Sponsorship tools',
        'Tournament organization'
      ],
      icon: <Crown className="h-6 w-6" />,
      description: 'Comprehensive solution for team owners and managers'
    },
    {
      id: 'venue',
      title: 'Venue Owner Account',
      price: '$29.99/month',
      popular: false,
      features: [
        'Venue listing management',
        'Booking system',
        'Revenue tracking',
        'Event hosting tools',
        'Customer management',
        'Facility optimization'
      ],
      icon: <MapPin className="h-6 w-6" />,
      description: 'Everything you need to manage and monetize your sports venue'
    }
  ];

  const handleUpgrade = (planId: string) => {
    setSelectedPlan(planId);
    // TODO: Implement actual upgrade logic
    toast.success(`Upgrade to ${planId} plan initiated! This feature is coming soon.`);
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      'player': 'Player',
      'coach': 'Coach',
      'team-owner': 'Team Owner',
      'venue': 'Venue Owner',
      'fan': 'Fan',
      'parent': 'Parent',
      'child': 'Child Player',
      'creative': 'Creative Professional',
      'support': 'Support Staff'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-2">
              <Crown className="h-8 w-8 text-sports-green" />
              Upgrade Your Account
            </h1>
            <p className="text-gray-400 mb-6">
              Unlock powerful features to manage teams, players, and venues
            </p>
            
            {/* Current Account Info */}
            <Card className="bg-gray-800/50 border border-gray-700 max-w-md mx-auto">
              <CardContent className="p-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-gray-300">Current Account:</span>
                  <Badge 
                    variant="outline" 
                    className="border-gray-500 text-gray-300"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {getRoleDisplayName(currentRole || 'Unknown')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upgrade Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {upgradePlans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative bg-gradient-to-br from-gray-900 to-gray-800 border transition-all duration-300 hover:scale-105 ${
                  plan.popular 
                    ? 'border-sports-green shadow-lg shadow-sports-green/20' 
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-sports-green text-black font-semibold px-3 py-1">
                      <Star className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="flex justify-center mb-4">
                    <div className={`p-3 rounded-full ${
                      plan.popular ? 'bg-sports-green/20 text-sports-green' : 'bg-gray-700 text-gray-300'
                    }`}>
                      {plan.icon}
                    </div>
                  </div>
                  <CardTitle className="text-xl text-white mb-2">
                    {plan.title}
                  </CardTitle>
                  <div className="text-3xl font-bold text-sports-green mb-2">
                    {plan.price}
                  </div>
                  <p className="text-gray-400 text-sm">
                    {plan.description}
                  </p>
                </CardHeader>

                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-sm">
                        <Check className="h-4 w-4 text-sports-green flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleUpgrade(plan.id)}
                    className={`w-full font-semibold transition-all ${
                      plan.popular
                        ? 'bg-sports-green hover:bg-sports-green/90 text-black shadow-lg'
                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                    }`}
                  >
                    {plan.popular ? (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Upgrade Now
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        <ArrowRight className="h-4 w-4 mr-2" />
                        Select Plan
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Features Comparison */}
          <Card className="bg-gray-900/50 border border-gray-700">
            <CardHeader>
              <CardTitle className="text-white text-center">
                Why Upgrade Your Account?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
                <div className="space-y-2">
                  <Users className="h-8 w-8 text-sports-green mx-auto" />
                  <h3 className="text-white font-semibold">Team Management</h3>
                  <p className="text-gray-400 text-sm">
                    Manage rosters, track performance, and communicate with your team
                  </p>
                </div>
                <div className="space-y-2">
                  <Trophy className="h-8 w-8 text-sports-green mx-auto" />
                  <h3 className="text-white font-semibold">Game Organization</h3>
                  <p className="text-gray-400 text-sm">
                    Schedule games, track scores, and manage tournaments
                  </p>
                </div>
                <div className="space-y-2">
                  <Camera className="h-8 w-8 text-sports-green mx-auto" />
                  <h3 className="text-white font-semibold">Content Creation</h3>
                  <p className="text-gray-400 text-sm">
                    Upload videos, photos, and share team highlights
                  </p>
                </div>
                <div className="space-y-2">
                  <Megaphone className="h-8 w-8 text-sports-green mx-auto" />
                  <h3 className="text-white font-semibold">Advanced Tools</h3>
                  <p className="text-gray-400 text-sm">
                    Access analytics, financial tools, and premium features
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Support */}
          <div className="text-center">
            <p className="text-gray-400 mb-4">
              Need help choosing the right plan?
            </p>
            <Button 
              variant="outline" 
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default AccountUpgrade;